import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './Cart.module.scss';
import Container from '../../components/UI/Container/Container';
import { Divider, Grid, message, Space, Typography } from 'antd';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Headline from '../../components/UI/Headline/Headline';
import apex from '../../assets/images/apex-legends-icon-16.jpg';
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import Button from '../../components/UI/Button/Button';
import { ROUTES } from '../../components/AppRouter/AppRouter';
import { deleteFromCart, updateInCart, getCart } from '../../http/cart';
import { getUser } from '../../redux/selectors';
import AuthModalsProvider from '../../components/modals/AuthModalsProvider';

const Cart = ({ cart, user }) => {
  const [signInOpen, setSignInOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);

  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const breakpoints = Grid.useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();

  const totalPrice = cart.reduce(
    (acc, product) => acc + product.price * product.quantity,
    0
  );
  const totalAmount = cart.reduce(
    (acc, product) => acc + product.quantity,
    0
  );

  useEffect(() => {
    if (user.id) fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      await dispatch(getCart());
    } catch (error) {
      console.log(error);
    }
  };

  const onRemove = async (product) => {
    try {
      await dispatch(deleteFromCart(product.id, product.denominationId));
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to remove product from cart'
      });
    }
  };

  const incrementQuantity = async (product) => {
    try {
      await dispatch(
        updateInCart(product.id, product.denominationId, product.quantity + 1)
      );
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to update quantity'
      });
    }
  };

  const decrementQuantity = async (product) => {
    try {
      await dispatch(
        updateInCart(product.id, product.denominationId, product.quantity - 1)
      );
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to update quantity'
      });
    }
  };

  const onGoBack = () => {
    navigate(-1);
  };

  return (
    <Container style={{ marginTop: 140, marginBottom: 150 }}>
      {contextHolder}
      <Space
        onClick={onGoBack}
        size={10}
        style={{ cursor: 'pointer', marginBottom: 40 }}
      >
        <LeftOutlined style={{ fontSize: 18 }} color='#9989C7' />
        <Typography style={{ fontWeight: 400 }}>{t('button.back')}</Typography>
      </Space>
      <Headline>{t('cart.title')}</Headline>
      {cart.length === 0 && (
        <Typography
          style={{ fontSize: 24, marginTop: 20, fontStyle: 'italic' }}
        >
          {t('cart.empty')}
        </Typography>
      )}
      <Space direction='vertical' size={10} className={classes.cart}>
        {cart.map((item) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}
              key={item.id}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 20,
                  padding: '40px 0'
                }}
              >
                <div
                  className={classes.cart__img}
                  onClick={() =>
                    navigate({
                      pathname: ROUTES.PRODUCT_CARD,
                      search: `?product=${item.productId}`
                    })
                  }
                >
                  <img src={`${item.imgUrl || apex}`} alt='' />
                </div>
                <div className={classes.cart__item}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16
                    }}
                  >
                    <Typography
                      className={classes.cart__name}
                      style={{
                        fontSize: breakpoints.xs ? 18 : 24
                      }}
                      onClick={() =>
                        navigate({
                          pathname: ROUTES.PRODUCT_CARD,
                          search: `?product=${item.productId}`
                        })
                      }
                    >
                      {i18n.language === 'en' ? item?.name : item?.name_lv}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: breakpoints.xs ? 18 : 24,
                        lineHeight: '24px',
                        fontWeight: 400
                      }}
                    >
                      <b>Nominal:</b>{' '}
                      {`${i18n.language === 'en' ? item.denominationName : item.denominationName_lv} EUR ${item.price?.toFixed(2)}`}
                    </Typography>
                    {item.quantity >= item.stock && (
                      <Typography style={{ color: 'red', fontWeight: 700 }}>
                        {t('pages.productCard.outOfStock')}
                      </Typography>
                    )}
                  </div>
                  <Typography
                    style={{
                      fontSize: 24,
                      lineHeight: '20px',
                      flexShrink: 0,
                      fontWeight: 400
                    }}
                  >
                    EUR {item.price?.toFixed(2)}
                  </Typography>
                  <Space size={20}>
                    <div
                      className={`${classes.quantityBtn} ${item.quantity === 1 ? classes.disabled : ''}`}
                      onClick={
                        item.quantity === 1
                          ? null
                          : () => decrementQuantity(item)
                      }
                    >
                      -
                    </div>
                    <Typography
                      style={{
                        fontWeight: 400,
                        fontSize: breakpoints.xs ? 16 : 20
                      }}
                    >
                      {item.quantity}
                    </Typography>
                    <div
                      className={`${classes.quantityBtn} ${item.quantity >= item.stock ? classes.disabled : ''}`}
                      onClick={item.quantity >= item.stock ? null : () => incrementQuantity(item)}
                    >
                      +
                    </div>
                  </Space>
                  <Typography
                    style={{
                      fontSize: breakpoints.xs ? 18 : 24,
                      lineHeight: '20px',
                      flexShrink: 0,
                      fontWeight: 400
                    }}
                  >
                    EUR {(item.quantity * item.price)?.toFixed(2)}
                  </Typography>
                  <Space
                    onClick={() => onRemove(item)}
                    className={classes.cart__delete}
                  >
                    <DeleteOutlined
                      style={{ fontSize: breakpoints.xs ? 16 : 20 }}
                    />
                    <Typography
                      style={{
                        fontSize: breakpoints.xs ? 16 : 20,
                        lineHeight: '24px',
                        fontWeight: 400
                      }}
                    >
                      {t('button.delete')}
                    </Typography>
                  </Space>
                </div>
              </div>
              <Divider
                style={{
                  margin: 0,
                  borderBlockStart: '1px solid rgba(88, 66, 113, 1)'
                }}
              />
            </div>
          );
        })}
      </Space>
      {cart.length !== 0 && (
        <Typography
          style={{
            fontSize: 24,
            lineHeight: '28px',
            padding: '30px 0',
            width: '100%',
            textAlign: 'end',
            fontWeight: 400,
            color: totalPrice > 1000 ? 'red' : 'inherit'
          }}
        >
          <b>{t('etc.total')}:</b> EUR {totalPrice.toFixed(2)}
        </Typography>
      )}
      {totalPrice > 1000 && (
        <div>
          <Typography
            style={{
              fontSize: 24,
              lineHeight: '28px',
              padding: '30px 0',
              width: '100%',
              textAlign: 'end',
              fontWeight: 400,
              color: 'red'
            }}
          >
            {t('cart.limit.price')}
          </Typography>
        </div>
      )}
      {totalAmount > 5 && (
        <div>
          <Typography
            style={{
              fontSize: 24,
              lineHeight: '28px',
              padding: '30px 0',
              width: '100%',
              textAlign: 'end',
              fontWeight: 400,
              color: 'red'
            }}
          >
            {t('cart.limit.quantity')}
          </Typography>
        </div>
      )}
      {cart.length !== 0 && (
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
        >
          {user.id ? (
            <Button
              wide={breakpoints.xs}
              onClick={() => navigate(ROUTES.CHECKOUT)}
              disabled={totalAmount > 5 || totalPrice > 1000}
            >
              {t('cart.checkoutContinue')}
            </Button>
          ) : (
            <Button wide={breakpoints.xs} onClick={() => setSignInOpen(true)}>
              {t('cart.login')}
            </Button>
          )}
        </div>
      )}

      <AuthModalsProvider
        signInOpen={signInOpen}
        signUpOpen={signUpOpen}
        setSignInOpen={setSignInOpen}
        setSignUpOpen={setSignUpOpen}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  cart: state.games.cart,
  products: state.games.products,
  user: getUser(state)
});

export default connect(mapStateToProps)(Cart);
